import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Send, Download, Save, Plus, Trash2, Mail, CheckCircle, Loader2 } from "lucide-react";
import { format } from "date-fns";
import VisaPaymentPanel from "@/components/VisaPaymentPanel";
import VisaAccountingPanel from "@/components/VisaAccountingPanel";
import ReviewRequestButton from "@/components/ReviewRequestButton";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const QUOTE_STATUS_COLORS = {
  draft:    "bg-slate-100 text-slate-600",
  sent:     "bg-blue-100 text-blue-700",
  accepted: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  expired:  "bg-amber-100 text-amber-700",
};

const PAYMENT_COLLECTION_LABELS = {
  not_requested:  { label: "Not Requested",   cls: "bg-slate-100 text-slate-600" },
  payment_pending:{ label: "Payment Pending",  cls: "bg-blue-100 text-blue-700" },
  partially_paid: { label: "Partially Paid",   cls: "bg-amber-100 text-amber-700" },
  paid:           { label: "Paid",             cls: "bg-green-100 text-green-700" },
};

const CURRENCIES = ["EUR", "GBP", "USD", "AED"];
const CURRENCY_SYMBOLS = { EUR: "€", GBP: "£", USD: "$", AED: "د.إ" };

const SERVICES = [
  { num: "01", desc: "Visa application form completion" },
  { num: "02", desc: "Document review and checklist preparation" },
  { num: "03", desc: "Supporting document upload via VFS portal" },
  { num: "04", desc: "VFS biometric appointment booking" },
  { num: "05", desc: "Application status monitoring" },
  { num: "06", desc: "General assistance throughout the process" },
];

const EMPTY_TRAVELLER = { full_name: "", nationality: "" };

function buildVisaQuoteHtml({
  quoteNumber, quoteDate, customerName, customerEmail, customerPhone,
  destinationCountry, visaType, nationality, travellers,
  serviceFee, currency, validityDays, paymentTerms, customerNotes,
}) {
  const sym = CURRENCY_SYMBOLS[currency] || currency;
  const feeDisplay = Number(serviceFee).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const count = travellers.length;

  const customerRows = [
    ["Name", customerName],
    ["Email", customerEmail],
    ...(customerPhone ? [["Phone / WhatsApp", customerPhone]] : []),
    ["Nationality", nationality],
    ["Destination", `${destinationCountry} &mdash; ${visaType}`],
    ["Applicants", `${count} applicant${count !== 1 ? "s" : ""}`],
  ].map(([label, val], i) => {
    const bg = i % 2 === 0 ? "#ffffff" : "#f8fafc";
    return `<tr style="background:${bg};">
      <td style="padding:7px 14px;color:#6b7280;width:140px;border-top:1px solid #e2e8f0;font-size:12px;">${label}</td>
      <td style="padding:7px 14px;font-weight:600;color:#1f2937;border-top:1px solid #e2e8f0;font-size:12px;">${val}</td>
    </tr>`;
  }).join("");

  const namedTravellers = travellers.filter(t => t.full_name);
  const applicantRowsHtml = namedTravellers.map((t, i) => {
    const bg = i % 2 === 0 ? "#ffffff" : "#f8fafc";
    return `<tr style="background:${bg};">
      <td style="padding:7px 14px;font-size:11px;font-weight:700;color:#9ca3af;font-family:monospace;border-top:1px solid #e2e8f0;">${String(i + 1).padStart(2, "0")}</td>
      <td style="padding:7px 14px;font-size:12px;font-weight:600;color:#1f2937;border-top:1px solid #e2e8f0;">${t.full_name}</td>
      <td style="padding:7px 14px;font-size:12px;color:#6b7280;border-top:1px solid #e2e8f0;">${t.nationality || ""}</td>
    </tr>`;
  }).join("");

  const applicantBlock = namedTravellers.length > 0 ? `
    <tr><td style="padding:12px 40px 0;">
      <p style="font-size:10px;font-weight:800;color:#1e3a5f;text-transform:uppercase;letter-spacing:2px;border-left:3px solid #1e3a5f;padding-left:10px;margin:0 0 8px;">Applicants</p>
      <table style="width:100%;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;" cellpadding="0" cellspacing="0">
        <tr style="background:linear-gradient(90deg,#1e3a5f,#2d5986);">
          <td style="padding:7px 14px;font-size:10px;font-weight:800;color:#fff;text-transform:uppercase;letter-spacing:2px;width:40px;">#</td>
          <td style="padding:7px 14px;font-size:10px;font-weight:800;color:#fff;text-transform:uppercase;letter-spacing:2px;">Full Name</td>
          <td style="padding:7px 14px;font-size:10px;font-weight:800;color:#fff;text-transform:uppercase;letter-spacing:2px;">Nationality</td>
        </tr>
        ${applicantRowsHtml}
      </table>
    </td></tr>` : "";

  const serviceRows = SERVICES.map((s, i) => {
    const bg = i % 2 === 0 ? "#ffffff" : "#f8fafc";
    return `<tr style="background:${bg};">
      <td style="padding:7px 14px;font-size:11px;font-weight:700;color:#1e3a5f;font-family:monospace;border-top:1px solid #e2e8f0;width:40px;">${s.num}</td>
      <td style="padding:7px 14px;font-size:12px;color:#374151;border-top:1px solid #e2e8f0;">${s.desc}</td>
    </tr>`;
  }).join("");

  const exclusionItems = [
    "UK Government visa application fees (payable directly to UKVI)",
    "VFS Global service charges (payable at the VFS centre)",
    "Priority or super priority processing services",
    "Document translation and notarization costs",
    "Courier or document delivery charges",
  ];
  const exclusionRows = exclusionItems.map((e) =>
    `<tr><td style="padding:6px 14px;font-size:12px;color:#78350f;border-top:1px solid #fef3c7;">&bull;&nbsp; ${e}</td></tr>`
  ).join("");

  const notesHtml = customerNotes ? `
  <tr><td style="padding:10px 40px 0;">
    <div style="background:#f0f9ff;border-left:3px solid #0ea5e9;border-radius:6px;padding:10px 14px;">
      <p style="margin:0;font-size:12px;color:#0c4a6e;"><strong>Note:</strong> ${customerNotes}</p>
    </div>
  </td></tr>` : "";

  const paymentHtml = paymentTerms ? `
  <tr><td style="padding:10px 40px 0;">
    <table style="width:100%;border:1px solid #e2e8f0;border-radius:8px;" cellpadding="0" cellspacing="0">
      <tr><td style="padding:8px 14px;background:#f8fafc;border-bottom:1px solid #e2e8f0;">
        <p style="margin:0;font-size:10px;font-weight:700;color:#1e3a5f;text-transform:uppercase;letter-spacing:1.5px;">Payment Terms</p>
      </td></tr>
      <tr><td style="padding:8px 14px;font-size:12px;color:#374151;">${paymentTerms}</td></tr>
    </table>
  </td></tr>` : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Visa Quotation ${quoteNumber}</title>
  <style>
    @page { size: A4 portrait; margin: 10mm; }
    * { box-sizing: border-box; }
    body { margin: 0; padding: 6px; background: #f3f4f6; font-family: Helvetica, Arial, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    p { margin: 0; }
  </style>
</head>
<body>
<table style="max-width:640px;margin:0 auto;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(109,40,217,0.15);" cellpadding="0" cellspacing="0" width="100%">

  <!-- HEADER -->
  <tr><td style="background:#ffffff;padding:16px 40px 12px;text-align:center;border-bottom:3px solid #1e3a5f;">
    <img src="https://thebetterfare.com/images/logo.png" alt="The Better Fare" style="height:44px;width:auto;display:block;margin:0 auto 8px;">
    <div style="display:inline-block;background:#f0f4ff;border:1px solid #bfcfff;border-radius:20px;padding:3px 14px;margin-bottom:6px;">
      <span style="font-size:10px;font-weight:700;color:#1e3a5f;text-transform:uppercase;letter-spacing:2px;">Visa Quotation</span>
    </div>
    <p style="margin:0;font-size:20px;font-weight:800;color:#1e3a5f;font-family:monospace;letter-spacing:4px;">${quoteNumber}</p>
    <p style="margin:3px 0 0;font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:1px;">Quote Reference &nbsp;&middot;&nbsp; ${quoteDate}</p>
  </td></tr>

  <!-- GREETING -->
  <tr><td style="padding:12px 40px 0;">
    <p style="font-size:15px;color:#1f2937;font-weight:700;margin:0 0 4px;">Dear ${customerName},</p>
    <p style="font-size:12px;color:#6b7280;line-height:1.6;margin:0;">Thank you for your interest in our ${destinationCountry} Visa Assistance Service. Please find below your personalised quotation.</p>
  </td></tr>

  <!-- CUSTOMER DETAILS -->
  <tr><td style="padding:12px 40px 0;">
    <p style="font-size:10px;font-weight:800;color:#1e3a5f;text-transform:uppercase;letter-spacing:2px;border-left:3px solid #1e3a5f;padding-left:10px;margin:0 0 8px;">Customer Details</p>
    <table style="width:100%;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;" cellpadding="0" cellspacing="0">
      ${customerRows}
    </table>
  </td></tr>

  <!-- APPLICANTS + PROFESSIONAL FEE: no page break between them -->
  <tr><td style="page-break-inside:avoid;">
    <table style="width:100%;" cellpadding="0" cellspacing="0">
      ${applicantBlock}
      <tr><td style="padding:12px 40px 0;">
        <p style="font-size:10px;font-weight:800;color:#1e3a5f;text-transform:uppercase;letter-spacing:2px;border-left:3px solid #1e3a5f;padding-left:10px;margin:0 0 8px;">Professional Fee</p>
        <table style="width:100%;background:#f0f4ff;border:1px solid #bfcfff;border-radius:10px;" cellpadding="0" cellspacing="0">
          <tr><td style="padding:14px 22px;text-align:center;">
            <p style="font-size:10px;font-weight:700;color:#1e3a5f;text-transform:uppercase;letter-spacing:2px;margin-bottom:6px;">Total Service Fee</p>
            <p style="font-size:40px;font-weight:900;color:#1e3a5f;font-family:monospace;letter-spacing:2px;line-height:1;margin:0;">
              <span style="font-size:20px;font-weight:700;vertical-align:super;">${sym}</span>${feeDisplay}
            </p>
            <p style="font-size:12px;font-weight:600;color:#374151;margin-top:6px;">for ${count} applicant${count !== 1 ? "s" : ""}</p>
            <p style="font-size:11px;color:#6b7280;margin-top:3px;">Valid for ${validityDays} days from date of issue &nbsp;&middot;&nbsp; Government &amp; VFS fees not included</p>
          </td></tr>
        </table>
      </td></tr>
    </table>
  </td></tr>

  <!-- INCLUDED SERVICES -->
  <tr><td style="padding:12px 40px 0;">
    <p style="font-size:10px;font-weight:800;color:#1e3a5f;text-transform:uppercase;letter-spacing:2px;border-left:3px solid #1e3a5f;padding-left:10px;margin:0 0 8px;">Included Services</p>
    <table style="width:100%;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;" cellpadding="0" cellspacing="0">
      <tr style="background:linear-gradient(90deg,#1e3a5f,#2d5986);">
        <td style="padding:7px 14px;font-size:10px;font-weight:800;color:#fff;text-transform:uppercase;letter-spacing:2px;width:40px;">#</td>
        <td style="padding:7px 14px;font-size:10px;font-weight:800;color:#fff;text-transform:uppercase;letter-spacing:2px;">Service Description</td>
      </tr>
      ${serviceRows}
    </table>
  </td></tr>

  <!-- NOT INCLUDED -->
  <tr><td style="padding:12px 40px 0;">
    <p style="font-size:10px;font-weight:800;color:#92400e;text-transform:uppercase;letter-spacing:2px;border-left:3px solid #f59e0b;padding-left:10px;margin:0 0 8px;">Not Included</p>
    <table style="width:100%;border:1px solid #fcd34d;border-radius:8px;overflow:hidden;" cellpadding="0" cellspacing="0">
      <tr style="background:#fffbeb;">
        <td style="padding:7px 14px;font-size:10px;font-weight:800;color:#92400e;text-transform:uppercase;letter-spacing:1.5px;border-bottom:1px solid #fcd34d;">The following are not covered by this quotation</td>
      </tr>
      ${exclusionRows}
    </table>
  </td></tr>

  <!-- IMPORTANT NOTICE -->
  <tr><td style="padding:12px 40px 0;">
    <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px 16px;">
      <p style="font-size:10px;font-weight:800;color:#1e3a5f;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:6px;">Important Notice</p>
      <p style="font-size:12px;color:#6b7280;line-height:1.6;margin-bottom:5px;">Visa approval remains solely at the discretion of the relevant immigration authority. The Better Fare provides professional application assistance only and cannot guarantee any visa outcome.</p>
      <p style="font-size:12px;color:#6b7280;line-height:1.6;">This quotation is valid for <strong style="color:#374151;">${validityDays} days</strong> from the date of issue. Fees are subject to revision if the scope changes before the engagement is confirmed.</p>
    </div>
  </td></tr>

  <!-- CUSTOMER NOTES -->
  ${notesHtml}

  <!-- PAYMENT TERMS -->
  ${paymentHtml}

  <!-- CONTACT -->
  <tr><td style="padding:10px 40px 0;text-align:center;">
    <p style="font-size:11px;color:#6b7280;line-height:1.8;">Need assistance? &nbsp;<strong>Email:</strong> <a href="mailto:info@thebetterfare.com" style="color:#6C3084;text-decoration:none;">info@thebetterfare.com</a> &nbsp;&middot;&nbsp; <strong>Website:</strong> <a href="https://www.thebetterfare.com" style="color:#6C3084;text-decoration:none;">www.thebetterfare.com</a></p>
  </td></tr>

  <!-- FOOTER -->
  <tr><td style="background:linear-gradient(135deg,#1e1b4b,#312e81);padding:14px 40px;text-align:center;margin-top:10px;">
    <p style="margin:0 0 3px;color:rgba(255,255,255,0.95);font-size:13px;font-weight:700;letter-spacing:0.5px;">The Better Fare &nbsp;&middot;&nbsp; Travel Agency</p>
    <p style="margin:0 0 2px;color:rgba(255,255,255,0.6);font-size:11px;">www.thebetterfare.com &nbsp;&middot;&nbsp; info@thebetterfare.com</p>
    <p style="margin:0;color:rgba(255,255,255,0.35);font-size:10px;">KvK 88159620</p>
  </td></tr>

</table>
</body></html>`;
}

export default function VisaQuoteGenerator() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [startingWork, setStartingWork] = useState(false);
  const [markingAccepted, setMarkingAccepted] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [linkedInvoice, setLinkedInvoice] = useState(null);

  const [visaRequest, setVisaRequest] = useState(null);
  const [quote, setQuote] = useState(null);

  const [quoteNumberOverride, setQuoteNumberOverride] = useState("");

  const today = format(new Date(), "d MMMM yyyy");

  const [form, setForm] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    destinationCountry: "",
    visaType: "",
    nationality: "",
    travellers: [{ ...EMPTY_TRAVELLER }],
    serviceFee: "",
    currency: "EUR",
    validityDays: 7,
    paymentTerms: "",
    internalNotes: "",
    customerNotes: "",
    travelDate: "",
  });
  const [sendEmail, setSendEmail] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [reqRes, quoteRes] = await Promise.all([
          axios.get(`${API}/visa-requests`, { headers: getAuthHeaders() }),
          axios.get(`${API}/visa-quotes?visa_request_id=${requestId}`, { headers: getAuthHeaders() }),
        ]);
        const req = (reqRes.data || []).find(r => r.id === requestId);
        if (!req) { toast.error("Visa request not found"); navigate("/visa-requests"); return; }
        setVisaRequest(req);
        setSendEmail(req.email || "");

        const travellers = (req.travellers || []).length > 0
          ? req.travellers.map(t => ({ full_name: t.full_name || "", nationality: t.nationality || "" }))
          : [{ ...EMPTY_TRAVELLER }];

        const base = {
          customerName: `${req.first_name} ${req.last_name}`,
          customerEmail: req.email || "",
          customerPhone: req.phone || "",
          destinationCountry: req.destination_country || "",
          visaType: req.visa_type || "",
          nationality: req.nationality || "",
          travellers,
          serviceFee: "",
          currency: "EUR",
          validityDays: 7,
          paymentTerms: "",
          internalNotes: "",
          customerNotes: req.customer_notes || "",
          travelDate: req.travel_date || "",
        };

        const existing = (quoteRes.data || [])[0];
        if (existing) {
          setQuote(existing);
          setSendEmail(existing.customer_email || req.email || "");
          setQuoteNumberOverride(existing.quote_number || "");
          if (existing.invoice_generated || existing.work_started) {
            try {
              const invRes = await axios.get(`${API}/invoices?source_type=visa_quote&source_id=${existing.id}`, { headers: getAuthHeaders() });
              const invList = invRes.data || [];
              if (invList.length > 0) setLinkedInvoice(invList[0]);
            } catch {}
          }
          setForm({
            customerName: existing.customer_name || base.customerName,
            customerEmail: existing.customer_email || base.customerEmail,
            customerPhone: existing.customer_phone || base.customerPhone,
            destinationCountry: existing.destination_country || base.destinationCountry,
            visaType: existing.visa_type || base.visaType,
            nationality: existing.nationality || base.nationality,
            travellers: (existing.travellers || []).length > 0 ? existing.travellers : travellers,
            serviceFee: existing.service_fee != null ? String(existing.service_fee) : "",
            currency: existing.currency || "EUR",
            validityDays: existing.validity_days || 7,
            paymentTerms: existing.payment_terms || "",
            internalNotes: existing.internal_notes || "",
            customerNotes: existing.customer_notes || "",
            travelDate: existing.travel_date || base.travelDate,
          });
        } else {
          setForm(base);
        }
      } catch (e) {
        toast.error("Failed to load visa request");
        navigate("/visa-requests");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [requestId]);

  const addTraveller = () => setForm(f => ({ ...f, travellers: [...f.travellers, { ...EMPTY_TRAVELLER }] }));
  const removeTraveller = i => setForm(f => ({ ...f, travellers: f.travellers.filter((_, idx) => idx !== i) }));
  const updateTraveller = (i, field, val) => setForm(f => {
    const t = [...f.travellers];
    t[i] = { ...t[i], [field]: val };
    return { ...f, travellers: t };
  });

  const buildQuoteHtmlFromForm = () => buildVisaQuoteHtml({
    quoteNumber:        quoteNumberOverride || quote?.quote_number || "DRAFT",
    quoteDate:          today,
    customerName:       form.customerName,
    customerEmail:      form.customerEmail,
    customerPhone:      form.customerPhone,
    destinationCountry: form.destinationCountry,
    visaType:           form.visaType,
    nationality:        form.nationality,
    travellers:         form.travellers,
    serviceFee:         form.serviceFee || 0,
    currency:           form.currency,
    validityDays:       form.validityDays,
    paymentTerms:       form.paymentTerms,
    customerNotes:      form.customerNotes,
  });

  const toApiPayload = () => ({
    visa_request_id:    requestId,
    customer_name:      form.customerName,
    customer_email:     form.customerEmail,
    customer_phone:     form.customerPhone || null,
    destination_country: form.destinationCountry,
    visa_type:          form.visaType,
    nationality:        form.nationality,
    travellers:         form.travellers,
    service_fee:        parseFloat(form.serviceFee) || 0,
    currency:           form.currency,
    validity_days:      parseInt(form.validityDays) || 7,
    payment_terms:      form.paymentTerms || null,
    internal_notes:     form.internalNotes || null,
    customer_notes:     form.customerNotes || null,
    travel_date:        form.travelDate || null,
    ...(quoteNumberOverride ? { quote_number: quoteNumberOverride } : {}),
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      let saved;
      if (quote) {
        const res = await axios.patch(`${API}/visa-quotes/${quote.id}`, toApiPayload(), { headers: getAuthHeaders() });
        saved = res.data;
      } else {
        const res = await axios.post(`${API}/visa-quotes`, toApiPayload(), { headers: getAuthHeaders() });
        saved = res.data;
      }
      setQuote(saved);
      setQuoteNumberOverride(saved.quote_number || "");
      toast.success(quote ? "Quote updated" : "Quote saved as draft");
      return saved;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const saved = await handleSave();
      if (!saved) return;
      const html = buildVisaQuoteHtml({
        ...{
          quoteNumber: quoteNumberOverride || saved.quote_number,
          quoteDate: today,
          customerName: form.customerName,
          customerEmail: form.customerEmail,
          customerPhone: form.customerPhone,
          destinationCountry: form.destinationCountry,
          visaType: form.visaType,
          nationality: form.nationality,
          travellers: form.travellers,
          serviceFee: form.serviceFee || 0,
          currency: form.currency,
          validityDays: form.validityDays,
          paymentTerms: form.paymentTerms,
          customerNotes: form.customerNotes,
        }
      });
      const res = await axios.post(
        `${API}/tickets/generate-pdf`,
        { recipient_email: "x@x.com", recipient_name: "", pnr: saved.quote_number, html_content: html, notification_type: "ticket" },
        { headers: getAuthHeaders(), responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Visa-Quotation-${saved.quote_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const buildEmailHtml = (savedQuote) => buildVisaQuoteHtml({
    quoteNumber: quoteNumberOverride || savedQuote.quote_number,
    quoteDate: today,
    customerName: form.customerName,
    customerEmail: form.customerEmail,
    customerPhone: form.customerPhone,
    destinationCountry: form.destinationCountry,
    visaType: form.visaType,
    nationality: form.nationality,
    travellers: form.travellers,
    serviceFee: form.serviceFee || 0,
    currency: form.currency,
    validityDays: form.validityDays,
    paymentTerms: form.paymentTerms,
    customerNotes: form.customerNotes,
  });

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      const saved = await handleSave();
      if (!saved) { setSendingTest(false); return; }
      await axios.post(
        `${API}/visa-quotes/${saved.id}/send-email`,
        { html_content: buildEmailHtml(saved), recipient_email: sendEmail.trim(), quote_number: quoteNumberOverride || saved.quote_number, customer_name: form.customerName, is_test: true },
        { headers: getAuthHeaders() }
      );
      const now = new Date().toISOString();
      setQuote(q => ({ ...q, test_email_sent: true, test_email_sent_at: now }));
      toast.success("Test quotation email sent to info@thebetterfare.com");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test email failed");
    } finally {
      setSendingTest(false);
    }
  };

  const handleMarkAccepted = async () => {
    if (!quote) return;
    setMarkingAccepted(true);
    try {
      const res = await axios.post(`${API}/visa-quotes/${quote.id}/mark-accepted`, {}, { headers: getAuthHeaders() });
      setQuote(res.data);
      toast.success("Quote marked as accepted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark as accepted");
    } finally {
      setMarkingAccepted(false);
    }
  };

  const handleStartWork = async () => {
    if (!quote) return;
    setStartingWork(true);
    try {
      const res = await axios.post(`${API}/visa-quotes/${quote.id}/start-work`, {}, { headers: getAuthHeaders() });
      setQuote(res.data);
      toast.success("Invoice generated — TBF number assigned");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate invoice");
    } finally {
      setStartingWork(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!quote) return;
    setCreatingInvoice(true);
    try {
      const res = await axios.post(`${API}/invoices/from-source`, {
        source_type: "visa_quote",
        source_id: quote.id,
      }, { headers: getAuthHeaders() });
      setLinkedInvoice(res.data);
      toast.success("Customer invoice created");
      navigate(`/invoices/${res.data.id}`);
    } catch (e) {
      if (e.response?.status === 409) {
        const existing = e.response.data?.invoice;
        if (existing) {
          setLinkedInvoice(existing);
          toast.info("Invoice already exists — opening it");
          navigate(`/invoices/${existing.id}`);
        }
      } else {
        toast.error(e.response?.data?.detail || "Failed to create invoice");
      }
    } finally {
      setCreatingInvoice(false);
    }
  };

  const reloadQuote = async () => {
    if (!quote?.id) return;
    try {
      const res = await axios.get(`${API}/visa-quotes?visa_request_id=${requestId}`, { headers: getAuthHeaders() });
      const updated = (res.data || [])[0];
      if (updated) setQuote(updated);
    } catch {}
  };

  const confirmSendCustomerEmail = async () => {
    setShowConfirm(false);
    if (!sendEmail.trim()) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      const saved = await handleSave();
      if (!saved) { setSending(false); return; }
      await axios.post(
        `${API}/visa-quotes/${saved.id}/send-email`,
        { html_content: buildEmailHtml(saved), recipient_email: sendEmail.trim(), quote_number: quoteNumberOverride || saved.quote_number, customer_name: form.customerName, is_test: false },
        { headers: getAuthHeaders() }
      );
      const now = new Date().toISOString();
      setQuote(q => ({ ...q, status: "sent", email_sent: true, email_sent_at: now }));
      toast.success(`Quotation email sent to ${sendEmail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email send failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    </DashboardLayout>
  );

  const f = form;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">

        {/* Page header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/visa-requests")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-serif text-2xl font-bold text-slate-900">Visa Quotation</h1>
              {quote && (
                <span className="font-mono text-sm font-bold text-purple-700">{quote.quote_number}</span>
              )}
              {quote && (
                <Badge className={QUOTE_STATUS_COLORS[quote.status] || "bg-slate-100 text-slate-600"}>
                  {quote.status}
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              {f.customerName} &middot; {f.customerEmail}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />PDF
            </Button>
            <Button variant="outline" onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save Draft"}
            </Button>
          </div>
        </div>

        {/* Workflow progress */}
        {quote && (
          <div className="mb-4 space-y-2">

            {/* Step 1: Mark Accepted — only if not yet accepted */}
            {quote.status !== "accepted" && !quote.work_started && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-green-800">Customer accepted the quote?</p>
                  <p className="text-xs text-green-600 mt-0.5">Records acceptance. Invoice will be generated separately.</p>
                </div>
                <Button className="shrink-0 bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleMarkAccepted} disabled={markingAccepted}>
                  {markingAccepted
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                    : <><CheckCircle className="w-4 h-4 mr-2" />Mark as Accepted</>}
                </Button>
              </div>
            )}

            {/* Step 2: Generate Invoice — accepted but no invoice yet */}
            {quote.status === "accepted" && !quote.invoice_generated && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-blue-800">Generate invoice</p>
                  <p className="text-xs text-blue-600 mt-0.5">Assigns a TBF-XXX invoice number and opens the bookkeeping panel.</p>
                </div>
                <Button className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleStartWork} disabled={startingWork}>
                  {startingWork
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                    : "Generate Invoice"}
                </Button>
              </div>
            )}

            {/* Invoice generated status bar */}
            {quote.invoice_generated && (
              <div className="space-y-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Invoice generated</p>
                    {quote.invoice_generated_at && (
                      <p className="text-xs text-emerald-600">{format(new Date(quote.invoice_generated_at), "d MMM yyyy")}</p>
                    )}
                  </div>
                  {quote.accounting?.invoice_number && (
                    <span className="ml-auto font-mono text-xs font-bold text-emerald-700">{quote.accounting.invoice_number}</span>
                  )}
                  {quote.payment_collection_status && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${(PAYMENT_COLLECTION_LABELS[quote.payment_collection_status] || PAYMENT_COLLECTION_LABELS.not_requested).cls}`}>
                      {(PAYMENT_COLLECTION_LABELS[quote.payment_collection_status] || PAYMENT_COLLECTION_LABELS.not_requested).label}
                    </span>
                  )}
                </div>
                {/* Customer invoice link or recreate button */}
                {linkedInvoice ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-purple-800">Customer Invoice</p>
                      <p className="text-xs text-purple-600 font-mono mt-0.5">{linkedInvoice.invoice_number}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 border-purple-300 text-purple-700 hover:bg-purple-100 text-xs"
                      onClick={() => navigate(`/invoices/${linkedInvoice.id}`)}
                    >
                      Open Invoice
                    </Button>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Customer Invoice Missing</p>
                      <p className="text-xs text-amber-600 mt-0.5">The invoice was deleted or not yet created.</p>
                    </div>
                    <Button
                      size="sm"
                      className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs"
                      onClick={handleCreateInvoice}
                      disabled={creatingInvoice}
                    >
                      {creatingInvoice
                        ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Creating...</>
                        : "Recreate Invoice"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Backward compat: work_started but no invoice_generated flag (old records) */}
            {quote.work_started && !quote.invoice_generated && (
              <div className="space-y-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Work in progress</p>
                    {quote.work_started_at && (
                      <p className="text-xs text-emerald-600">Started {format(new Date(quote.work_started_at), "d MMM yyyy")}</p>
                    )}
                  </div>
                  {quote.accounting?.invoice_number && (
                    <span className="ml-auto font-mono text-xs font-bold text-emerald-700">{quote.accounting.invoice_number}</span>
                  )}
                </div>
                {linkedInvoice ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-purple-800">Customer Invoice</p>
                      <p className="text-xs text-purple-600 font-mono mt-0.5">{linkedInvoice.invoice_number}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 border-purple-300 text-purple-700 hover:bg-purple-100 text-xs"
                      onClick={() => navigate(`/invoices/${linkedInvoice.id}`)}
                    >
                      Open Invoice
                    </Button>
                  </div>
                ) : (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-purple-800">Customer Invoice</p>
                      <p className="text-xs text-purple-600 mt-0.5">Create a customer-facing invoice for this quote.</p>
                    </div>
                    <Button
                      size="sm"
                      className="shrink-0 bg-purple-700 hover:bg-purple-800 text-white text-xs"
                      onClick={handleCreateInvoice}
                      disabled={creatingInvoice}
                    >
                      {creatingInvoice
                        ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Creating...</>
                        : "Create Invoice"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Email actions card */}
        <div className="mb-5 bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide shrink-0 w-16">Send to:</span>
            <Input
              value={sendEmail}
              onChange={e => setSendEmail(e.target.value)}
              placeholder="customer@email.com"
              className="h-8 text-sm flex-1"
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={handleSendTestEmail}
              disabled={sendingTest || saving}
            >
              <Mail className="w-4 h-4 mr-2" />
              {sendingTest ? "Sending test…" : "Send Test Email to Myself"}
            </Button>
            <Button
              size="sm"
              className="flex-1 bg-purple-700 hover:bg-purple-800 text-white"
              onClick={() => { if (!sendEmail.trim()) { toast.error("Enter a recipient email"); return; } setShowConfirm(true); }}
              disabled={sending || saving}
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? "Sending…" : "Send Quote to Customer"}
            </Button>
          </div>
          {showConfirm && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between gap-3">
              <p className="text-sm text-amber-800 font-medium">
                Send this quotation to <strong>{sendEmail}</strong>?
              </p>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
                <Button size="sm" className="bg-purple-700 hover:bg-purple-800 text-white" onClick={confirmSendCustomerEmail}>Confirm Send</Button>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-5">

          {/* ── LEFT: FORM ─────────────────────────────────── */}
          <div className="col-span-2 space-y-5">

            {/* Customer details */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <h2 className="font-semibold text-slate-700 text-xs uppercase tracking-widest">Customer Details</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Full Name</Label>
                  <Input value={f.customerName} onChange={e => setForm(p => ({ ...p, customerName: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <Input value={f.customerEmail} onChange={e => setForm(p => ({ ...p, customerEmail: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Phone / WhatsApp</Label>
                  <Input value={f.customerPhone} onChange={e => setForm(p => ({ ...p, customerPhone: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Nationality</Label>
                  <Input value={f.nationality} onChange={e => setForm(p => ({ ...p, nationality: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Destination Country</Label>
                  <Input value={f.destinationCountry} onChange={e => setForm(p => ({ ...p, destinationCountry: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Visa Type</Label>
                  <Input value={f.visaType} onChange={e => setForm(p => ({ ...p, visaType: e.target.value }))} className="mt-1" />
                </div>
              </div>
            </div>

            {/* Applicants */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-slate-700 text-xs uppercase tracking-widest">Applicants</h2>
                <Button size="sm" variant="outline" onClick={addTraveller}>
                  <Plus className="w-3 h-3 mr-1" />Add
                </Button>
              </div>
              <div className="space-y-2">
                {f.travellers.map((t, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <span className="text-xs font-mono text-slate-400 w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <Input
                      value={t.full_name}
                      onChange={e => updateTraveller(i, "full_name", e.target.value)}
                      placeholder="Full name"
                      className="h-9 text-sm"
                    />
                    <Input
                      value={t.nationality}
                      onChange={e => updateTraveller(i, "nationality", e.target.value)}
                      placeholder="Nationality"
                      className="h-9 text-sm w-36"
                    />
                    {f.travellers.length > 1 && (
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-red-400 hover:text-red-600 shrink-0" onClick={() => removeTraveller(i)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Quotation details */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <h2 className="font-semibold text-slate-700 text-xs uppercase tracking-widest">Quotation Details</h2>
              <div>
                <Label className="text-xs text-slate-500">Quote Reference</Label>
                <Input
                  value={quoteNumberOverride}
                  onChange={e => setQuoteNumberOverride(e.target.value)}
                  placeholder={quote ? quote.quote_number : `Auto-generated on first save (e.g. VISA-${new Date().getFullYear()}-001)`}
                  className="mt-1 font-mono"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Service Fee *</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                      {CURRENCY_SYMBOLS[f.currency] || f.currency}
                    </span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={f.serviceFee}
                      onChange={e => setForm(p => ({ ...p, serviceFee: e.target.value }))}
                      className="pl-7"
                      placeholder="249"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Currency</Label>
                  <Select value={f.currency} onValueChange={v => setForm(p => ({ ...p, currency: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Valid for (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={f.validityDays}
                    onChange={e => setForm(p => ({ ...p, validityDays: parseInt(e.target.value) || 7 }))}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Payment Terms (optional)</Label>
                <Input
                  value={f.paymentTerms}
                  onChange={e => setForm(p => ({ ...p, paymentTerms: e.target.value }))}
                  placeholder="e.g. 50% upfront, 50% on submission"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <h2 className="font-semibold text-slate-700 text-xs uppercase tracking-widest">Notes</h2>
              <div>
                <Label className="text-xs text-slate-500">Customer Notes <span className="text-slate-400">(shown on PDF)</span></Label>
                <textarea
                  value={f.customerNotes}
                  onChange={e => setForm(p => ({ ...p, customerNotes: e.target.value }))}
                  rows={3}
                  placeholder="Any notes visible to the customer..."
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Internal Notes <span className="text-slate-400">(not shown on PDF)</span></Label>
                <textarea
                  value={f.internalNotes}
                  onChange={e => setForm(p => ({ ...p, internalNotes: e.target.value }))}
                  rows={2}
                  placeholder="Internal reference, pricing notes..."
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                />
              </div>
            </div>

          </div>

          {/* ── RIGHT: LIVE PREVIEW ────────────────────────── */}
          <div className="col-span-1">
            <div className="sticky top-6">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Live Preview</p>
                <div className="rounded-lg overflow-hidden border border-slate-100" style={{ height: "540px" }}>
                  <iframe
                    srcDoc={buildQuoteHtmlFromForm()}
                    style={{ width: "250%", height: "1350px", border: "none", transformOrigin: "top left", transform: "scale(0.4)" }}
                    title="visa-quote-preview"
                  />
                </div>
              </div>

              {/* Quote meta */}
              {quote && (
                <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4 space-y-2.5 text-xs text-slate-500">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-700">Status</span>
                    <span className={`inline-block px-2 py-0.5 rounded-full font-medium ${QUOTE_STATUS_COLORS[quote.status] || ""}`}>{quote.status}</span>
                  </div>
                  {quote.accounting?.invoice_number && (
                    <div className="flex items-center justify-between border-t border-slate-100 pt-2">
                      <span>Invoice</span>
                      <span className="font-mono text-xs font-bold text-slate-700">{quote.accounting.invoice_number}</span>
                    </div>
                  )}
                  {quote.invoice_generated && (
                    <div className="flex items-center justify-between border-t border-slate-100 pt-2">
                      <span>Payment</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${(PAYMENT_COLLECTION_LABELS[quote.payment_collection_status] || PAYMENT_COLLECTION_LABELS.not_requested).cls}`}>
                        {(PAYMENT_COLLECTION_LABELS[quote.payment_collection_status] || PAYMENT_COLLECTION_LABELS.not_requested).label}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-slate-100 pt-2 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span>Test email sent</span>
                      <span className={`font-medium ${quote.test_email_sent ? "text-green-600" : "text-slate-400"}`}>
                        {quote.test_email_sent ? "Yes" : "No"}
                      </span>
                    </div>
                    {quote.test_email_sent_at && (
                      <p className="text-slate-400 text-right">{format(new Date(quote.test_email_sent_at), "d MMM yyyy, HH:mm")}</p>
                    )}
                  </div>
                  <div className="border-t border-slate-100 pt-2 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span>Customer email sent</span>
                      <span className={`font-medium ${quote.email_sent ? "text-green-600" : "text-slate-400"}`}>
                        {quote.email_sent ? "Yes" : "No"}
                      </span>
                    </div>
                    {quote.email_sent_at && (
                      <p className="text-slate-400 text-right">{format(new Date(quote.email_sent_at), "d MMM yyyy, HH:mm")}</p>
                    )}
                  </div>
                  <p className="border-t border-slate-100 pt-2 text-slate-400">
                    Created {quote.created_at ? format(new Date(quote.created_at), "d MMM yyyy") : "—"}
                  </p>
                </div>
              )}

              {/* Payment panel — shown when invoice has been generated */}
              {(quote?.work_started || quote?.invoice_generated) && (
                <div className="mt-4">
                  <VisaPaymentPanel
                    quote={quote}
                    quoteId={quote.id}
                    onUpdate={reloadQuote}
                  />
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Accounting panel — shown when invoice has been generated */}
        {(quote?.work_started || quote?.invoice_generated) && (
          <div className="mt-6 bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-700 text-xs uppercase tracking-widest">Bookkeeping</h2>
              {quote.accounting?.invoice_number && (
                <div className="text-xs text-slate-500 flex items-center gap-3">
                  <span>Quote: <span className="font-mono font-semibold text-slate-700">{quote.quote_number}</span></span>
                  <span>Invoice: <span className="font-mono font-semibold text-violet-700">{quote.accounting.invoice_number}</span></span>
                </div>
              )}
            </div>
            <VisaAccountingPanel
              quoteId={quote.id}
              quote={quote}
              onUpdate={reloadQuote}
            />
            <div className="bg-white rounded-xl border border-slate-200 p-5 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Customer Review</h2>
              </div>
              <ReviewRequestButton
                sourceType="visa_quote"
                sourceId={quote?.id || requestId}
                customerName={form.customerName}
                customerEmail={form.customerEmail}
              />
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
